/**
 * @fullvsl/app-shell/views/publish — finalize-deployment view (FUL-238).
 *
 * Follows the approved publish_vsl reference screen: the full MVP workflow now
 * includes a dedicated publish surface (setup → config → preview → analytics →
 * publish). Shows the async embed snippet with copy, integration status (Full
 * Funnel custom menu), a projected-lift card, the CSS-selector reveal
 * instructions, and a live/maintenance status toggle.
 *
 * Iframe-safe: clipboard uses the async Clipboard API with a graceful fallback
 * (select + execCommand) so it works under restrictive sandboxes; no top-window
 * access.
 */
import type { VslProject } from "../../../contracts/src/contracts";
import type { VslStore } from "../store";
import { el, append, icon, ICONS } from "../dom";

export function renderPublishView(
  store: VslStore,
  projectId: string,
  navigate: (path: string) => void,
): { mount: HTMLElement; cleanup: () => void } {
  const project = store.get(projectId);
  const wrapper = el("div");

  if (!project) {
    const empty = el("div", "fva-empty");
    append(empty, el("h3", null, "VSL não encontrada"));
    append(wrapper, empty);
    return { mount: wrapper, cleanup: () => {} };
  }

  // ---- Page header ----
  const header = el("div", "fva-page-header");
  const headerText = el("div");
  append(headerText, el("h2", null, "Finalizar publicação"));
  append(headerText, el("p", null, "Sua VSL está pronta para ser publicada. Escolha abaixo como deseja disponibilizá-la."));
  append(header, headerText);

  // Publicada / Manutenção toggle (segmented control, visual-only status flag).
  const toggle = el("div", "fva-segmented");
  const liveBtn = el("button", "fva-segmented-btn is-active", "Publicada") as HTMLButtonElement;
  const maintBtn = el("button", "fva-segmented-btn", "Manutenção") as HTMLButtonElement;
  liveBtn.type = "button";
  maintBtn.type = "button";
  liveBtn.addEventListener("click", () => {
    liveBtn.classList.add("is-active");
    maintBtn.classList.remove("is-active");
  });
  maintBtn.addEventListener("click", () => {
    maintBtn.classList.add("is-active");
    liveBtn.classList.remove("is-active");
  });
  append(toggle, liveBtn, maintBtn);
  append(header, toggle);
  append(wrapper, header);

  // ---- Bento grid ----
  const grid = el("div", "fva-publish-grid");

  // Embed code card (wide)
  grid.appendChild(renderEmbedCard(project, navigate));

  // Side column: integration status + projected lift
  const side = el("div", "fva-publish-side");
  side.appendChild(renderIntegrationStatus());
  side.appendChild(renderProjectedLift());
  append(grid, side);

  // CSS selector reveal instructions (wide)
  grid.appendChild(renderRevealInstructions(project));

  append(wrapper, grid);

  return { mount: wrapper, cleanup: () => {} };
}

function renderEmbedCard(project: VslProject, navigate: (path: string) => void): HTMLElement {
  const card = el("div", "fva-card fva-publish-embed fva-card-pad");
  const head = el("div", "fva-publish-embed-head");
  const headLeft = el("div", "fva-publish-embed-head-left");
  append(headLeft, icon(ICONS.code, "fva-publish-embed-icon"));
  append(headLeft, el("h3", "fva-publish-h3", "Código de incorporação"));
  append(head, headLeft);
  append(head, el("span", "fva-tag", "JavaScript assíncrono"));

  const snippetBox = el("div", "fva-snippet-box");
  const copyBtn = el("button", "fva-snippet-copy-btn", "Copiar código") as HTMLButtonElement;
  copyBtn.type = "button";
  append(copyBtn, icon(ICONS.copy, "fva-nav-icon"));
  const pre = el("pre", "fva-snippet");

  const embedOrigin = typeof location !== "undefined" ? location.origin : "https://fullvsl.example.com";
  const snippet = `<!-- Incorporação FullVSL para ${project.name} -->
<div data-fullvsl="${project.id}"></div>
<script async src="${embedOrigin}/embed/fullvsl.js"></script>`;
  pre.textContent = snippet;

  copyBtn.addEventListener("click", () => {
    const done = () => {
      copyBtn.textContent = "Copiado ✓";
      setTimeout(() => {
        copyBtn.textContent = "";
        append(copyBtn, icon(ICONS.copy, "fva-nav-icon"));
        append(copyBtn, document.createTextNode("Copiar código"));
      }, 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(snippet).then(done, () => fallbackCopy(snippet, done));
    } else {
      fallbackCopy(snippet, done);
    }
  });

  append(snippetBox, copyBtn, pre);
  const hintRow = el("div", "fva-publish-hint-row");
  append(
    hintRow,
    hintCell("Contêiner de destino", "Local onde o vídeo deve aparecer na página."),
    hintCell("Inserção do script", "Adicione uma vez por página no <body> ou <head>."),
  );

  append(card, head, snippetBox, hintRow);

  // Link back to the live player preview.
  const previewLink = el("button", "fva-btn fva-btn-secondary fva-btn-sm", "Abrir visualização do player →") as HTMLButtonElement;
  previewLink.type = "button";
  previewLink.addEventListener("click", () => navigate(`#/vsl/${project.id}/preview`));
  append(card, previewLink);

  return card;
}

function hintCell(title: string, body: string): HTMLElement {
  const c = el("div", "fva-publish-hint");
  append(c, el("span", "fva-publish-hint-label", title));
  append(c, el("p", "fva-publish-hint-body", body));
  return c;
}

function renderIntegrationStatus(): HTMLElement {
  const card = el("div", "fva-card fva-card-pad");
  append(card, el("h3", "fva-publish-section-title", "Status das integrações"));

  const ffRow = el("div", "fva-integ-row");
  const ffLeft = el("div", "fva-integ-row-left");
  append(ffLeft, el("span", "fva-integ-dot fva-integ-dot-on"));
  append(ffLeft, el("span", "fva-integ-name", "Menu personalizado do Full Funnel"));
  append(ffRow, ffLeft);
  append(ffRow, icon(ICONS.checkCircle, "fva-integ-check"));
  append(card, ffRow);

  const pixelRow = el("div", "fva-integ-row fva-integ-row-off");
  const pixelLeft = el("div", "fva-integ-row-left");
  append(pixelLeft, el("span", "fva-integ-dot fva-integ-dot-off"));
  append(pixelLeft, el("span", "fva-integ-name", "Pixel do Facebook"));
  append(pixelRow, pixelLeft);
  append(pixelRow, el("span", "fva-integ-status", "Desconectado"));
  append(card, pixelRow);

  const manageBtn = el("button", "fva-btn fva-btn-secondary fva-btn-sm fva-btn-block", "Gerenciar integrações") as HTMLButtonElement;
  manageBtn.type = "button";
  append(card, manageBtn);
  return card;
}

function renderProjectedLift(): HTMLElement {
  const card = el("div", "fva-publish-lift");
  const bg = icon(ICONS.rocket, "fva-publish-lift-bg");
  const body = el("div", "fva-publish-lift-body");
  append(body, el("h3", "fva-publish-section-title", "Ganho estimado"));
  const valRow = el("div", "fva-publish-lift-valrow");
  append(valRow, el("span", "fva-publish-lift-value", "+24.8%"));
  append(valRow, el("span", "fva-publish-lift-sub", "Taxa de conversão"));
  append(body, valRow);
  append(body, el("p", "fva-publish-lift-note", "Estimativa baseada na retenção atual e nos momentos de exibição configurados."));
  append(card, bg, body);
  return card;
}

function renderRevealInstructions(project: VslProject): HTMLElement {
  const card = el("div", "fva-card fva-card-pad fva-publish-reveal");
  const head = el("div", "fva-publish-reveal-head");
  append(head, icon(ICONS.visibilityOff, "fva-publish-reveal-icon"));
  append(head, el("h3", "fva-publish-h3", "Exibição por seletor CSS"));
  append(card, head);

  const steps = el("div", "fva-publish-steps");
  const revealAt = project.cta.revealAt ?? 50;
  const revealLabel =
    project.cta.revealMode === "timestamp" ? `${Math.floor(revealAt / 60)}:${String(revealAt % 60).padStart(2, "0")} (${revealAt}s)` : `${revealAt}% assistido`;

  append(
    steps,
    revealStep("1", "Identifique seus elementos", [
      "Adicione a classe CSS ",
      code(".f-vsl-hide"),
      " a qualquer elemento que deve permanecer oculto até o momento de exibição.",
    ]),
    revealStep("2", "Defina o momento de exibição", [
      "Na configuração da VSL, a exibição está definida para ",
      code(revealLabel),
      ". Nesse momento, a CTA e o preço serão exibidos.",
    ]),
    revealStep("3", "Verificação", [
      "O mecanismo do FullVSL controla ",
      code("display: none"),
      " e aplica automaticamente uma transição suave ao exibir os elementos.",
    ]),
  );
  append(card, steps);
  return card;
}

function revealStep(n: string, title: string, bodyParts: (string | HTMLElement)[]): HTMLElement {
  const step = el("div", "fva-publish-step");
  const badge = el("div", "fva-publish-step-badge", n);
  const stepBody = el("div");
  append(stepBody, el("h4", "fva-publish-step-title", title));
  const p = el("p", "fva-publish-step-body");
  for (const part of bodyParts) {
    if (typeof part === "string") p.appendChild(document.createTextNode(part));
    else p.appendChild(part);
  }
  append(stepBody, p);
  append(step, badge, stepBody);
  return step;
}

function code(text: string): HTMLElement {
  const c = el("code", "fva-inline-code", text);
  return c;
}

function fallbackCopy(text: string, done: () => void): void {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    done();
  } catch {
    // Best-effort; clipboard may be blocked in the iframe sandbox.
  }
}
